/**
 * Webtech HR Engine™ - Employee Campaign Detail Page
 * Détails de la performance d'un employé dans une campagne
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  User,
  Target,
  Star,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  Calendar,
  TrendingUp,
  Award,
  FileText,
  MessageSquare,
  ChevronRight
} from 'lucide-react';

// Score gauge component
const ScoreGauge = ({ score, size = 'lg' }) => {
  const getScoreColor = (score) => {
    if (score >= 4) return 'text-green-600 bg-green-100';
    if (score >= 3) return 'text-blue-600 bg-blue-100';
    if (score >= 2) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };
  
  const getScoreLabel = (score) => {
    if (score >= 4) return 'Excellent';
    if (score >= 3) return 'Satisfaisant';
    if (score >= 2) return 'À améliorer';
    return 'Insuffisant';
  };
  
  const sizeClass = size === 'lg' ? 'w-20 h-20 text-2xl' : 'w-12 h-12 text-sm';
  
  return (
    <div className="text-center">
      <div className={`${sizeClass} rounded-full ${getScoreColor(score)} flex items-center justify-center font-bold mx-auto`}>
        {score?.toFixed(1) || '-'}
      </div>
      <p className="text-xs text-slate-500 mt-1">{getScoreLabel(score)}</p>
    </div>
  );
};

export default function EmployeeCampaignDetailPage() {
  const { employeeId, campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = user?.companyId;

  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [evaluations, setEvaluations] = useState({
    self: null,
    manager: null,
    peers: []
  });
  const [synthesis, setSynthesis] = useState(null);

  useEffect(() => {
    if (companyId && employeeId && campaignId) {
      loadData();
    }
  }, [companyId, employeeId, campaignId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load employee
      const empRes = await api.get(`/employees/${employeeId}`);
      setEmployee(empRes.data);
      
      // Load campaign
      const campaignRes = await api.get(`/performance/campaigns/${campaignId}`);
      setCampaign(campaignRes.data);
      
      // Load objectives for this employee
      const objRes = await api.get(`/performance/objectives?company_id=${companyId}&employee_id=${employeeId}&campaign_id=${campaignId}`);
      setObjectives(objRes.data.objectives || []);
      
      // Load evaluations for this employee
      const evalRes = await api.get(`/performance/evaluations?company_id=${companyId}&employee_id=${employeeId}&campaign_id=${campaignId}`);
      const evals = evalRes.data.evaluations || [];
      
      setEvaluations({
        self: evals.find(e => e.type === 'self'),
        manager: evals.find(e => e.type === 'manager'),
        peers: evals.filter(e => e.type === 'peer')
      });
      
      // Load synthesis if exists
      const synthRes = await api.get(`/performance/synthesis/${employeeId}/${campaignId}`).catch(() => null);
      if (synthRes?.data) {
        setSynthesis(synthRes.data);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Détail employé">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  if (!employee || !campaign) {
    return (
      <DashboardLayout title="Évaluation">
        <div className="max-w-lg mx-auto text-center py-16">
          <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-amber-500" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">
            Aucune évaluation à afficher
          </h3>
          <p className="text-slate-600 mb-6 leading-relaxed">
            {!employee 
              ? "Cet employé n'est pas encore assigné à cette campagne d'évaluation. Vérifiez que l'employé a été ajouté comme participant."
              : "Cette campagne d'évaluation n'existe pas ou a été supprimée."
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <Button onClick={() => navigate('/performance')} className="bg-webtech-blue hover:bg-webtech-blue-hover text-white">
              Voir les campagnes
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Calculate progress
  const objValidated = objectives.filter(o => o.status === 'validated').length;
  const progressPercent = objectives.length > 0 ? Math.round((objValidated / objectives.length) * 100) : 0;

  return (
    <DashboardLayout title={`${employee.firstName} ${employee.lastName}`}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <Separator orientation="vertical" className="h-8" />
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-webtech-blue flex items-center justify-center text-white text-xl font-semibold">
                {employee.firstName?.[0]}{employee.lastName?.[0]}
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">
                  {employee.firstName} {employee.lastName}
                </h1>
                <p className="text-slate-500">{employee.jobTitle || employee.position}</p>
              </div>
            </div>
          </div>
          
          {synthesis?.finalScore && (
            <ScoreGauge score={synthesis.finalScore} size="lg" />
          )}
        </div>

        {/* Campaign Info */}
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">{campaign.name}</p>
                  <p className="text-sm text-slate-500">
                    {new Date(campaign.startDate).toLocaleDateString('fr-FR')} - {new Date(campaign.endDate).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
              <Badge className={campaign.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                {campaign.status === 'active' ? 'En cours' : campaign.status === 'closed' ? 'Terminée' : campaign.status}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Progress Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{objValidated}/{objectives.length}</p>
                  <p className="text-sm text-slate-500">Objectifs validés</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${evaluations.self ? 'bg-green-100' : 'bg-slate-100'} flex items-center justify-center`}>
                  <User className={`w-5 h-5 ${evaluations.self ? 'text-green-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{evaluations.self ? '✓' : '✗'}</p>
                  <p className="text-sm text-slate-500">Auto-évaluation</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${evaluations.manager ? 'bg-green-100' : 'bg-slate-100'} flex items-center justify-center`}>
                  <Star className={`w-5 h-5 ${evaluations.manager ? 'text-green-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{evaluations.manager ? '✓' : '✗'}</p>
                  <p className="text-sm text-slate-500">Éval. Manager</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${synthesis ? 'bg-purple-100' : 'bg-slate-100'} flex items-center justify-center`}>
                  <Award className={`w-5 h-5 ${synthesis ? 'text-purple-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{synthesis?.finalScore?.toFixed(1) || '-'}</p>
                  <p className="text-sm text-slate-500">Score final</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="objectives">
          <TabsList>
            <TabsTrigger value="objectives">Objectifs ({objectives.length})</TabsTrigger>
            <TabsTrigger value="evaluations">Évaluations</TabsTrigger>
            <TabsTrigger value="synthesis">Synthèse</TabsTrigger>
          </TabsList>

          {/* Objectives Tab */}
          <TabsContent value="objectives" className="mt-4 space-y-4">
            {objectives.length === 0 ? (
              <Card className="py-8">
                <CardContent className="text-center">
                  <Target className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Aucun objectif défini pour cet employé</p>
                </CardContent>
              </Card>
            ) : (
              objectives.map(obj => (
                <Card key={obj.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={
                            obj.status === 'validated' ? 'bg-green-100 text-green-700' :
                            obj.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {obj.status === 'validated' ? 'Validé' : obj.status === 'pending' ? 'En attente' : obj.status}
                          </Badge>
                          <Badge variant="outline">{obj.category || 'Performance'}</Badge>
                        </div>
                        <h3 className="font-semibold text-slate-900 mb-1">{obj.title}</h3>
                        <p className="text-sm text-slate-500 mb-2">{obj.description}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-400">
                          <span>Poids: {obj.weight || 1}</span>
                          <span>Échéance: {new Date(obj.deadline || campaign.endDate).toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>
                      {obj.score && (
                        <div className="text-center ml-4">
                          <div className="text-2xl font-bold text-webtech-blue">{obj.score}/5</div>
                          <p className="text-xs text-slate-500">Score</p>
                        </div>
                      )}
                    </div>
                    {obj.progress !== undefined && (
                      <div className="mt-4">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-slate-500">Progression</span>
                          <span className="font-medium">{obj.progress}%</span>
                        </div>
                        <Progress value={obj.progress} className="h-2" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Evaluations Tab */}
          <TabsContent value="evaluations" className="mt-4 space-y-4">
            {/* Self Evaluation */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-500" />
                  Auto-évaluation
                </CardTitle>
              </CardHeader>
              <CardContent>
                {evaluations.self ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Score global</span>
                      <span className="text-lg font-bold text-blue-600">{evaluations.self.score?.toFixed(1) || '-'}/5</span>
                    </div>
                    {evaluations.self.comment && (
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-sm text-slate-600">{evaluations.self.comment}</p>
                      </div>
                    )}
                    <p className="text-xs text-slate-400">
                      Soumis le {new Date(evaluations.self.submittedAt || evaluations.self.updatedAt).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-slate-500">
                    <Clock className="w-5 h-5" />
                    <span>En attente</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Manager Evaluation */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  Évaluation Manager
                </CardTitle>
              </CardHeader>
              <CardContent>
                {evaluations.manager ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Score global</span>
                      <span className="text-lg font-bold text-amber-600">{evaluations.manager.score?.toFixed(1) || '-'}/5</span>
                    </div>
                    {evaluations.manager.comment && (
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-sm text-slate-600">{evaluations.manager.comment}</p>
                      </div>
                    )}
                    <p className="text-xs text-slate-400">
                      Soumis le {new Date(evaluations.manager.submittedAt || evaluations.manager.updatedAt).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-slate-500">
                    <Clock className="w-5 h-5" />
                    <span>En attente</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Peer Evaluations (if 360°) */}
            {campaign.mode === '360' && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-purple-500" />
                    Évaluations 360° ({evaluations.peers.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {evaluations.peers.length > 0 ? (
                    <div className="space-y-3">
                      {evaluations.peers.map((peer, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <span className="text-sm">Évaluateur {idx + 1}</span>
                          <span className="font-medium text-purple-600">{peer.score?.toFixed(1) || '-'}/5</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 text-slate-500">
                      <Clock className="w-5 h-5" />
                      <span>Aucune évaluation 360° reçue</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Synthesis Tab */}
          <TabsContent value="synthesis" className="mt-4">
            {synthesis ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-webtech-blue" />
                    Synthèse de l'évaluation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Final Score */}
                  <div className="flex items-center justify-center">
                    <ScoreGauge score={synthesis.finalScore} size="lg" />
                  </div>
                  
                  {/* Score breakdown */}
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{synthesis.selfScore?.toFixed(1) || '-'}</p>
                      <p className="text-xs text-slate-500">Auto-évaluation</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">{synthesis.managerScore?.toFixed(1) || '-'}</p>
                      <p className="text-xs text-slate-500">Manager</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{synthesis.objectivesScore?.toFixed(1) || '-'}</p>
                      <p className="text-xs text-slate-500">Objectifs</p>
                    </div>
                  </div>
                  
                  {/* Comments */}
                  {synthesis.summary && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-2">Commentaire général</h4>
                      <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-4">{synthesis.summary}</p>
                    </div>
                  )}
                  
                  {synthesis.strengths && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-2">Points forts</h4>
                      <p className="text-sm text-slate-600 bg-green-50 rounded-lg p-4">{synthesis.strengths}</p>
                    </div>
                  )}
                  
                  {synthesis.areasToImprove && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-2">Axes d'amélioration</h4>
                      <p className="text-sm text-slate-600 bg-amber-50 rounded-lg p-4">{synthesis.areasToImprove}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="py-12">
                <CardContent className="text-center">
                  <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Pas de synthèse disponible</h3>
                  <p className="text-slate-500">La synthèse sera générée une fois toutes les évaluations complétées.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
